/**
 * Acessibilidade (SPEC 9.6.8, 10.7 — WCAG 2.2 AA).
 *
 * - `announce()`: única porta para `aria-live`. Polite só para toasts de venda, novas P0/P1 e o
 *   resumo opcional; assertive só para o banner P0. KPIs NUNCA são anunciados a cada atualização.
 * - Foco: armadilha para sheets/popovers e devolução à origem.
 * - Preferências do sistema: movimento, transparência, contraste.
 */
import type { KpiFormat, Ms } from '@rota/contracts';
import { useSyncExternalStore } from 'react';
import { ariaValue } from './format';
import { partsSP } from './time';

/** Classe global (styles/base.css) para conteúdo só de leitor de tela. */
export const SR_ONLY_CLASS = 'srOnly';
/** Id do `<main>` usado pelo skip link "Pular para o conteúdo". */
export const MAIN_ID = 'conteudo';

// ---------------------------------------------------------------------------
// Live regions
// ---------------------------------------------------------------------------

export type Politeness = 'polite' | 'assertive';
const regionIds: Record<Politeness, string> = {
  polite: 'rota-live-polite',
  assertive: 'rota-live-assertive',
};
const pending: Record<Politeness, ReturnType<typeof setTimeout> | null> = { polite: null, assertive: null };

function ensureRegion(p: Politeness): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById(regionIds[p]);
  if (!el) {
    el = document.createElement('div');
    el.id = regionIds[p];
    el.className = SR_ONLY_CLASS;
    el.setAttribute('aria-live', p);
    el.setAttribute('aria-atomic', 'true');
    el.setAttribute('role', p === 'assertive' ? 'alert' : 'status');
    document.body.appendChild(el);
  }
  return el;
}

/**
 * Anuncia uma mensagem para leitores de tela. Mensagens próximas são unidas
 * (uma por quadro), para não interromper a leitura.
 */
export function announce(message: string, politeness: Politeness = 'polite'): void {
  const el = ensureRegion(politeness);
  if (!el || !message) return;
  const prev = pending[politeness];
  if (prev) clearTimeout(prev);
  el.textContent = '';
  pending[politeness] = setTimeout(() => {
    el.textContent = message;
    pending[politeness] = null;
  }, 60);
}

// ---------------------------------------------------------------------------
// Foco
// ---------------------------------------------------------------------------

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Elementos focáveis visíveis dentro do container, em ordem de tabulação. */
export function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && !el.closest('[inert]') && el.getClientRects().length > 0,
  );
}

export interface FocusTrap {
  release(): void;
}

/**
 * Prende o foco dentro do container (sheets, popovers, paleta) e devolve o foco à
 * origem ao soltar (10.7). Tab e Shift+Tab circulam; o foco inicial vai para o
 * primeiro elemento com `[data-autofocus]`, ou o primeiro focável, ou o container.
 */
export function trapFocus(container: HTMLElement, opts: { restoreTo?: HTMLElement | null } = {}): FocusTrap {
  const origin =
    opts.restoreTo ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  const initial =
    container.querySelector<HTMLElement>('[data-autofocus]') ?? getFocusable(container)[0] ?? container;
  if (initial === container && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
  initial.focus({ preventScroll: true });

  const onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Tab') return;
    const items = getFocusable(container);
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !container.contains(active))) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && (active === last || !container.contains(active))) {
      e.preventDefault();
      first?.focus();
    }
  };
  document.addEventListener('keydown', onKey, true);
  return {
    release() {
      document.removeEventListener('keydown', onKey, true);
      if (origin?.isConnected) origin.focus({ preventScroll: true });
    },
  };
}

/** Move o foco para o `<main>` (troca de rota): leitores de tela anunciam a nova página. */
export function focusMain(): void {
  if (typeof document === 'undefined') return;
  const main = document.getElementById(MAIN_ID);
  if (!main) return;
  if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
  main.focus({ preventScroll: true });
}

// ---------------------------------------------------------------------------
// Preferências do sistema
// ---------------------------------------------------------------------------

export const MEDIA = {
  reducedMotion: '(prefers-reduced-motion: reduce)',
  reducedTransparency: '(prefers-reduced-transparency: reduce)',
  moreContrast: '(prefers-contrast: more)',
  forcedColors: '(forced-colors: active)',
  dark: '(prefers-color-scheme: dark)',
  coarse: '(pointer: coarse)',
  compact: '(max-width: 599.98px)',
  medium: '(min-width: 600px) and (max-width: 1023.98px)',
  expanded: '(min-width: 1024px)',
  large: '(min-width: 1440px)',
  tv: '(min-width: 1920px)',
} as const;

export function matches(query: string): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(query).matches
  );
}

export function prefersReducedMotion(): boolean {
  return matches(MEDIA.reducedMotion);
}

function subscribeMedia(query: string, cb: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const mql = window.matchMedia(query);
  mql.addEventListener('change', cb);
  return () => mql.removeEventListener('change', cb);
}

/** Hook reativo para media queries (breakpoints e preferências). */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (cb) => subscribeMedia(query, cb),
    () => matches(query),
    () => false,
  );
}

export function useReducedMotion(): boolean {
  return useMediaQuery(MEDIA.reducedMotion);
}

export type Breakpoint = 'compact' | 'medium' | 'expanded' | 'large';

/** Faixa de viewport (9.7): compacto < 600 · médio 600–1023 · expandido 1024–1439 · grande ≥ 1440. */
export function useBreakpoint(): Breakpoint {
  const large = useMediaQuery(MEDIA.large);
  const expanded = useMediaQuery(MEDIA.expanded);
  const medium = useMediaQuery(MEDIA.medium);
  if (large) return 'large';
  if (expanded) return 'expanded';
  if (medium) return 'medium';
  return 'compact';
}

// ---------------------------------------------------------------------------
// Textos para leitor de tela
// ---------------------------------------------------------------------------

/** "14 horas e 32 minutos" (datas e horas por extenso em aria-label; 10.7). */
export function spokenTime(ms: Ms): string {
  const { hour, minute } = partsSP(ms);
  const h = `${hour} ${hour === 1 ? 'hora' : 'horas'}`;
  if (minute === 0) return h;
  return `${h} e ${minute} ${minute === 1 ? 'minuto' : 'minutos'}`;
}

export interface ChartDescription {
  /** "linha", "barras", "área", "dispersão"… */
  kind: string;
  /** "faturamento acumulado de hoje" */
  subject: string;
  format: KpiFormat;
  min?: number | null;
  max?: number | null;
  last?: number | null;
  lastAt?: Ms | null;
}

/**
 * `aria-label` de gráfico (9.6.8): "Gráfico de linha, faturamento acumulado de hoje,
 * de 0 a R$ 15 mil, agora R$ 9.870 às 14 horas e 32 minutos".
 */
export function chartAriaLabel(d: ChartDescription): string {
  const parts = [`Gráfico de ${d.kind}`, d.subject];
  if (d.min !== undefined && d.max !== undefined && d.min !== null && d.max !== null) {
    parts.push(`de ${ariaValue(d.min, d.format)} a ${ariaValue(d.max, d.format)}`);
  }
  if (d.last !== undefined && d.last !== null) {
    const at = d.lastAt ? ` às ${spokenTime(d.lastAt)}` : '';
    parts.push(`agora ${ariaValue(d.last, d.format)}${at}`);
  }
  return parts.join(', ');
}
